/**
 * Shared sanitize-html allowlists for every dangerouslySetInnerHTML sink
 * that renders tenant- or user-authored markup (frontend and portal).
 *
 * Four tiers, matched to what each editor legitimately produces:
 *
 * - INLINE  — single-line chrome text (announcement bar, footer legal /
 *   about text) edited inline in the portal chrome editor.
 * - RICH_TEXT — TipTap output rendered by CMS sections (RichText.tsx).
 * - CONTENT — HugeRTE documents on public content routes (blog post
 *   bodies, FAQ answers, landing-page FAQ answers). Superset of
 *   RICH_TEXT that also permits images and basic alignment styles.
 * - AGREEMENT — legal agreement templates (body, header/footer/margin
 *   chrome, signature block). Deliberately the broadest tier: legal
 *   formatting relies on inline styles, tables, images and anchors.
 *
 * sanitize-html is a pure string-based parser (no DOM required), so these
 * work identically in Node SSR (frontend) and browser SPA (portal).
 *
 * The custom-trackers feature (TrackerSlot.tsx) is intentionally NOT
 * routed through this module — executing tenant script is its purpose.
 */

import sanitizeHtml from "sanitize-html";
import { VIDEO_EMBED_ALLOWLIST } from "./embedUtils";

/** Clamp colspan/rowspan to a sane maximum to prevent layout blowout. */
function clampSpanAttribs(
  tagName: string,
  attribs: sanitizeHtml.Attributes,
): sanitizeHtml.Tag {
  for (const attr of ["colspan", "rowspan"]) {
    if (attribs[attr]) {
      const n = parseInt(attribs[attr], 10);
      attribs[attr] = String(Math.min(Math.max(isNaN(n) ? 1 : n, 1), 12));
    }
  }
  return { tagName, attribs };
}

/**
 * Permissive style-value matcher that still refuses anything able to
 * trigger a request or legacy script evaluation from inside a property
 * value (url(), image-set(), expression()). The allowed property lists
 * below already exclude url-capable properties; this is defense in depth.
 */
const SAFE_STYLE_VALUE = [/^(?![\s\S]*(?:url|image-set|expression)\s*\()[\s\S]*$/i];

/** Build an allowedStyles property map where every listed prop accepts SAFE_STYLE_VALUE. */
function styleProps(props: string[]): Record<string, RegExp[]> {
  return Object.fromEntries(props.map((p) => [p, SAFE_STYLE_VALUE]));
}

// ── Inline chrome text ──────────────────────────────────────────────
// Announcement bar message, footer legal/about/disclaimer text, and the
// portal chrome-editor canvas previews of the same fields. Matches the
// inline marks TipTap produces (bold, italic, underline, links,
// font-size/color spans).

export const INLINE_SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: [
    "strong", "b", "em", "i", "u", "s", "a", "span", "br",
  ],
  allowedAttributes: {
    "*": ["class"],
    a: ["href", "target", "rel"],
    span: ["style"],
  },
  allowedStyles: {
    span: styleProps(["font-size", "color"]),
  },
  allowedSchemes: ["http", "https", "mailto"],
  disallowedTagsMode: "discard",
};

// ── TipTap rich text (CMS sections) ─────────────────────────────────
// Allowlist tuned to what TipTap actually produces. Everything not
// listed here is stripped on render.

const RICH_TEXT_TAGS = [
  "p", "br", "strong", "b", "em", "i", "u", "s",
  "a", "span", "ul", "ol", "li",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "blockquote", "code", "pre", "hr", "sub", "sup",
  // Table elements
  "table", "thead", "tbody", "tfoot", "tr", "th", "td",
  "caption", "colgroup", "col",
];

export const RICH_TEXT_SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: RICH_TEXT_TAGS,
  allowedAttributes: {
    "*": ["class"],
    a: ["href", "target", "rel"],
    th: ["colspan", "rowspan", "scope"],
    td: ["colspan", "rowspan"],
  },
  transformTags: {
    th: clampSpanAttribs,
    td: clampSpanAttribs,
  },
  allowedSchemes: ["http", "https", "mailto"],
  // Strip disallowed tags entirely (don't escape them as text)
  disallowedTagsMode: "discard",
};

// ── HugeRTE content (blog bodies, FAQ answers, landing FAQs) ────────
// Same shape as rich text plus what the blog/FAQ editors actually
// produce: CDN images (see hd-api blog image pruning, which parses
// body <img src> URLs) with align styles (float / display+margin),
// sized and shaded tables, the Format-menu typography (font-family,
// line-height, colors), the indent button's padding-left, and video
// iframes from the allowlisted embed hosts (authorable via the
// editors' source view).

const CONTENT_STYLE_PROPS = [
  "text-align", "padding-left", "line-height",
  "color", "background-color",
  "font-family", "font-size", "font-weight", "font-style", "text-decoration",
  "float", "display", "vertical-align",
  "margin", "margin-top", "margin-bottom", "margin-left", "margin-right",
  "width", "height", "max-width",
  "border", "border-width", "border-style", "border-color",
  "border-collapse", "border-spacing",
  "list-style-type",
];

export const CONTENT_SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: [...RICH_TEXT_TAGS, "img", "figure", "figcaption", "iframe"],
  allowedAttributes: {
    "*": ["class", "style"],
    a: ["href", "target", "rel"],
    th: ["colspan", "rowspan", "scope", "width"],
    td: ["colspan", "rowspan", "width"],
    table: ["border", "cellpadding", "cellspacing", "width"],
    col: ["span", "width"],
    img: ["src", "alt", "title", "width", "height", "loading"],
    iframe: [
      "src", "width", "height", "title",
      "frameborder", "allow", "allowfullscreen",
    ],
  },
  allowedStyles: {
    "*": styleProps(CONTENT_STYLE_PROPS),
  },
  transformTags: {
    th: clampSpanAttribs,
    td: clampSpanAttribs,
  },
  allowedSchemes: ["http", "https", "mailto", "tel"],
  // HugeRTE pastes images as base64 data URIs until they're uploaded.
  allowedSchemesByTag: { img: ["http", "https", "data"], iframe: ["https"] },
  // Video embeds only, from the same hosts the CMS embed section trusts.
  allowedIframeHostnames: [...VIDEO_EMBED_ALLOWLIST],
  // A rejected iframe src leaves an empty <iframe> shell behind — drop it.
  exclusiveFilter: (frame) => frame.tag === "iframe" && !frame.attribs.src,
  disallowedTagsMode: "discard",
};

// ── Agreements ──────────────────────────────────────────────────────
// Legal templates legitimately use inline styles (fonts, margins,
// alignment), tables and images (letterhead logos). Broadest style
// tier, but still: no script, no event handlers, no iframes, no url()
// in style values, http(s)/mailto/tel links only — and no class/id,
// which would let a template borrow the host app's utility CSS
// (.modal.is-active, .fixed.inset-0) for full-viewport UI redress or
// clobber getElementById targets in the booking flow.

const AGREEMENT_STYLE_PROPS = [
  "color", "background-color",
  "font-family", "font-size", "font-weight", "font-style", "font-variant",
  "text-align", "text-decoration", "text-transform", "text-indent",
  "line-height", "letter-spacing", "word-spacing", "white-space",
  "vertical-align",
  "margin", "margin-top", "margin-bottom", "margin-left", "margin-right",
  "padding", "padding-top", "padding-bottom", "padding-left", "padding-right",
  "width", "min-width", "max-width", "height", "min-height", "max-height",
  "display", "float", "clear", "opacity",
  "border", "border-top", "border-bottom", "border-left", "border-right",
  "border-width", "border-style", "border-color", "border-radius",
  "border-collapse", "border-spacing", "table-layout",
  "list-style-type", "list-style-position",
  "page-break-before", "page-break-after", "page-break-inside", "break-inside",
];

export const AGREEMENT_SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: [
    ...RICH_TEXT_TAGS,
    "img", "figure", "figcaption",
    "div", "small", "dl", "dt", "dd", "address",
  ],
  allowedAttributes: {
    "*": ["style"],
    a: ["href", "target", "rel"],
    th: ["colspan", "rowspan", "scope", "width", "align", "valign"],
    td: ["colspan", "rowspan", "width", "align", "valign"],
    table: ["border", "cellpadding", "cellspacing", "width", "align"],
    col: ["span", "width"],
    img: ["src", "alt", "title", "width", "height"],
  },
  allowedStyles: {
    "*": styleProps(AGREEMENT_STYLE_PROPS),
  },
  transformTags: {
    th: clampSpanAttribs,
    td: clampSpanAttribs,
  },
  allowedSchemes: ["http", "https", "mailto", "tel"],
  allowedSchemesByTag: { img: ["http", "https", "data"] },
  disallowedTagsMode: "discard",
};

// ── Helpers ─────────────────────────────────────────────────────────

/** Sanitize inline chrome text (announcement bar, footer legal/about text). */
export function sanitizeInlineHtml(html: string | null | undefined): string {
  return sanitizeHtml(html || "", INLINE_SANITIZE_OPTIONS);
}

/** Sanitize HugeRTE documents on public content routes (blog, FAQ, landing). */
export function sanitizeContentHtml(html: string | null | undefined): string {
  return sanitizeHtml(html || "", CONTENT_SANITIZE_OPTIONS);
}

/** Sanitize agreement HTML (body, header/footer/margin chrome, signature block). */
export function sanitizeAgreementHtml(html: string | null | undefined): string {
  return sanitizeHtml(html || "", AGREEMENT_SANITIZE_OPTIONS);
}

/**
 * Escape a plain-text value for interpolation into an HTML string.
 * Use this — not a sanitizer — for scalar text (names, labels): a
 * sanitizer parses its input as HTML and double-encodes entities the
 * text already contains.
 */
export function escapeHtml(text: string | null | undefined): string {
  return (text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
